import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Box, Flex, HStack, IconButton } from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { MdArrowUpward } from 'react-icons/md';
import AGGridBase from 'components/tables/AGGrid/AGGridBase';
import { Text, Tooltip } from '@/components/v4';
import {
    PANE_HEIGHT,
    PANE_MIN_HEIGHT,
    SCROLL_TOP_THRESHOLD,
    TOOLBAR_BAND_BG,
} from '../constants';
import { defaultColDef } from './tableConfig';

/**
 * One list on the bulk operations page, laid out like the Numbers page
 * table: a heading (with an optional info tooltip and a count), then a
 * bordered table with an optional grey toolbar band, the grid and a footer.
 * Both lists use this, so their borders and footers match in every view.
 */
const NumbersPane = ({
    title,
    info,
    count,
    toolbar,
    footer,
    gridRef,
    gridProps,
    ...boxProps
}) => {
    const { darkMode, colorScheme } = useSelector((state) => state.settings);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const bandBg = darkMode ? TOOLBAR_BAND_BG.dark : TOOLBAR_BAND_BG.light;

    return (
        <Flex
            direction="column"
            h={PANE_HEIGHT}
            minH={PANE_MIN_HEIGHT}
            minW={0}
            aria-label={title}
            {...boxProps}>
            <HStack spacing={2} mb={3}>
                <Text fontSize="md" fontWeight="500">
                    {title}
                </Text>
                {info && (
                    <Tooltip hasArrow placement="top" label={info}>
                        <InfoOutlineIcon
                            aria-label={`About ${title}`}
                            color={darkMode ? 'gray.300' : 'gray.500'}
                        />
                    </Tooltip>
                )}
                <Badge borderRadius="full" px={2} textTransform="none">
                    {count}
                </Badge>
            </HStack>

            <Flex
                direction="column"
                flex="1"
                minH={0}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="10px"
                overflow="hidden"
                // Set on the grid's own theme element, which otherwise applies
                // the material theme's purple selected-row colour. The dark
                // value is rgba(144, 205, 244, 0.16) as hex: Chakra splits
                // commas in custom property values.
                sx={{
                    '.ag-theme-base': {
                        '--ag-selected-row-background-color': darkMode
                            ? '#90CDF429'
                            : 'var(--chakra-colors-blue-50)',
                    },
                }}>
                {toolbar && <Box bg={bandBg}>{toolbar}</Box>}

                <Box flex="1" minH={0} position="relative">
                    <AGGridBase
                        gridRef={gridRef}
                        defaultColDef={defaultColDef}
                        pagination={false}
                        useFixedHeight={false}
                        customHeight="100%"
                        rowHeight={48}
                        onBodyScroll={(event) =>
                            setShowBackToTop(event.top > SCROLL_TOP_THRESHOLD)
                        }
                        {...gridProps}
                    />
                    {showBackToTop && (
                        <Tooltip label="Back to top" hasArrow placement="left">
                            <IconButton
                                aria-label="Back to top"
                                icon={<MdArrowUpward />}
                                isRound
                                size="sm"
                                colorScheme={colorScheme}
                                position="absolute"
                                right={4}
                                bottom={4}
                                zIndex={1}
                                boxShadow="md"
                                onClick={() =>
                                    gridRef.current?.api?.ensureIndexVisible(
                                        0,
                                        'top',
                                    )
                                }
                            />
                        </Tooltip>
                    )}
                </Box>

                <Flex
                    align="center"
                    justify="space-between"
                    gap={3}
                    px={4}
                    py={3}
                    bg={bandBg}
                    borderTop="1px solid"
                    borderColor={borderColor}>
                    {footer}
                </Flex>
            </Flex>
        </Flex>
    );
};

export default NumbersPane;
